import { registerCopy as copy } from "@/components/people/register/register-copy";
import type { PeopleAccessFacet, PeopleAttentionFacet, PeopleRegisterFilters, PeopleRelationshipFacet, PeopleStudentStatus, PeopleStudentView, PeopleTeachingFacet, PeopleView } from "@/lib/api/people";

export const PAGE_SIZES = [25, 50, 100] as const;

export const VIEWS: ReadonlyArray<{ value: string; label: string; view: PeopleView; student?: PeopleStudentView }> = [
  { value: "operational", label: copy.viewOperational, view: "operational" },
  { value: "all", label: copy.viewAll, view: "all" },
  { value: "archived", label: copy.viewArchived, view: "archived" },
  { value: "attention", label: copy.viewAttention, view: "attention" },
  { value: "current", label: copy.viewCurrent, view: "all", student: "current" },
  { value: "alumni", label: copy.viewAlumni, view: "all", student: "alumni" },
  { value: "history", label: copy.viewHistory, view: "all", student: "history" },
];

export const ACCESS: ReadonlyArray<[PeopleAccessFacet, string]> = [
  ["active", copy.accessFilterActive],
  ["deactivated", copy.accessFilterDeactivated],
  ["no_account", copy.accessFilterNone],
];

export const RELATIONSHIPS: ReadonlyArray<[PeopleRelationshipFacet, string]> = [
  ["student", copy.relationshipStudent],
  ["faculty", copy.relationshipFaculty],
  ["admin", copy.relationshipAdmin],
  ["staff_no_profile", copy.relationshipStaff],
];

export const ATTENTION: ReadonlyArray<[PeopleAttentionFacet, string]> = [
  ["unnamed_account", copy.attentionUnnamed],
  ["unlinked_student", copy.attentionUnlinked],
  ["profile_without_membership", copy.attentionProfile],
];

export const STATUSES: ReadonlyArray<[PeopleStudentStatus, string]> = [
  ["active", "Active"],
  ["leave_of_absence", "Leave of absence"],
  ["withdrawn", "Withdrawn"],
  ["completed", "Completed"],
];

export const TEACHING: ReadonlyArray<[PeopleTeachingFacet, string]> = [
  ["assigned", copy.teachingAssigned],
  ["unassigned", copy.teachingUnassigned],
];

function one<T extends string>(value: string | null, options: ReadonlyArray<readonly [T, string]>): T | undefined {
  return options.find(([option]) => option === value)?.[0];
}

function positive(value: string | null, fallback: number): number {
  if (!value || !/^\d+$/.test(value)) return fallback;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : fallback;
}

/** The register's reading from the address. Each filter takes one choice; an older link's first one is kept. */
export function peopleFiltersFromSearchParams(params: URLSearchParams, q: string): PeopleRegisterFilters {
  const studentView = one(params.get("student_view"), VIEWS.filter((view) => view.student).map((view) => [view.student!, view.label] as const));
  const requested = one(params.get("view"), [["operational", ""], ["archived", ""], ["attention", ""], ["retired", ""], ["all", ""]] as const);
  const access = one(params.get("access"), ACCESS);
  const relationship = one(params.get("relationship"), RELATIONSHIPS);
  const attention = one(params.get("attention"), ATTENTION);
  const pageSize = positive(params.get("page_size"), 25);
  return {
    // A historical deep link asks for historical scope, so it isn't intersected with the operational default.
    view: requested === "retired" ? "archived" : requested ?? (studentView ? "all" : "operational"),
    q: q.trim() || undefined,
    relationship: relationship ? [relationship] : [],
    access: access ? [access] : [],
    attention: attention ? [attention] : [],
    program_id: params.get("program_id") || undefined,
    cohort_id: params.get("cohort_id") || undefined,
    student_status: one(params.get("student_status"), STATUSES),
    student_view: studentView,
    teaching: one(params.get("teaching"), TEACHING),
    semester_id: params.get("semester_id") || undefined,
    page: positive(params.get("page"), 1),
    page_size: (PAGE_SIZES as readonly number[]).includes(pageSize) ? pageSize : 25,
  };
}
